import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "weblinks";

export type Weblink = {
  readonly id: string;
  readonly label: string;
  readonly url: string;
  readonly showInMenu: boolean;
};

function useWeblinksSyncModule(): SyncModule<ReadonlyArray<Weblink>> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Weblinks are a new-Datenportal-only resource — the legacy portal has
      // no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchWeblinksNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchWeblinksNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Weblink>> {
  const response = await fetch(`${baseUrl}/api/device/sync/web-contents`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.webContents ?? []).map(parseWeblinkNew);
}

function parseWeblinkNew(entry: any): Weblink {
  return {
    id: entry.id,
    label: entry.label,
    url: entry.url,
    showInMenu: entry.showInMenu ?? false,
  };
}

export function useWeblinksSync() {
  const module = useWeblinksSyncModule();
  useSyncModuleRegistration(module);
}

export function useWeblinks(): ReadonlyArray<Weblink> {
  return useSyncData(syncModuleId) ?? [];
}
